/**
 * Copyright (c) Omnissa, LLC. All rights reserved.
 * This product is protected by copyright and intellectual property laws in the
 * United States and other countries as well as by international treaties.
 * -- Omnissa Restricted
 *
 * @format
 */

import { Injectable } from "@angular/core";
import { ChromeServerModel } from "./chrome-server-model";
import { PolicyModel } from "../google-policy/policy-model";
import { LocalStorageService } from "../../core/services/storage/local-storage.service";

// After inited, will detect whether the window.onServerChosen is set, if yes,
// request to input the broker.

/**
 * All functions are wrapped into promise.
 * We don't want callback
 */

@Injectable({
   providedIn: "root"
})
export class ChromeServerService {
   private static readonly REGISTERED_SERVERS_KEY = "REGISTERED_SERVERS_KEY";
   private static readonly IP_REGEX =
      /^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/;
   private static readonly HOSTNAME_REGEX =
      /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/;
   private static readonly HTTPS_PREFIX = "https://";
   private static readonly HTTP_PREFIX = "http://";
   private hideSecondaryServer: boolean = true;
   constructor(
      private chromeServerModel: ChromeServerModel,
      private policyModel: PolicyModel,
      private localStorageService: LocalStorageService
   ) {}

   private setServer = (servers) => {
      if (servers) {
         this.localStorageService.set(ChromeServerService.REGISTERED_SERVERS_KEY, JSON.stringify(servers));
      } else {
         this.localStorageService.remove(ChromeServerService.REGISTERED_SERVERS_KEY);
      }
   };

   private getServer = () => {
      try {
         return JSON.parse(this.localStorageService.get(ChromeServerService.REGISTERED_SERVERS_KEY)) || [];
      } catch (e) {
         return [];
      }
   };

   public getRegisteredServers = () => {
      return new Promise((resolve, reject) => {
         let servers = this.getServer();
         if (!servers || servers.length === 0) {
            resolve([]);
         }
         resolve(servers);
      });
   };

   public registerNewServer = (server) => {
      if (!server) {
         throw "Null server object is passed in!";
      }

      return new Promise((resolve, reject) => {
         const model = this.chromeServerModel.getModel(
            server.address,
            server.autoConnect,
            server.description,
            server.secondaryServers,
            new Date().toISOString(),
            server.isTitan
         );

         let servers = this.getServer();
         if (!servers) {
            servers = [];
         }
         let update = false;
         for (let i = 0; i < servers.length; i++) {
            const tempServer = servers[i];
            if (model.equals(tempServer)) {
               // Already registered
               resolve(servers);
               return;
            } else if (model.sameEntry(tempServer)) {
               // always assign since we have cancel button in UI.
               tempServer["description"] = server.description || "";
               tempServer["isTitan"] = server.isTitan;
               update = true;
               break;
            }
         }

         if (!update) {
            servers.push(model);
         }

         this.setServer(servers);
         resolve(servers);
      });
   };

   public registerSecondaryServer = (originServer, secondaryServer: string) => {
      if (!originServer || !secondaryServer) {
         throw "Null server object is passed in!";
      }

      return new Promise((resolve, reject) => {
         const primaryModel = this.chromeServerModel.getModel(
            originServer.address,
            originServer.autoConnect,
            originServer.description,
            originServer.secondaryServers,
            originServer.updateTime,
            originServer.isTitan
         );

         secondaryServer = secondaryServer.toLowerCase().replace(/:\d+/, "");
         let servers = this.getServer();

         for (let i = 0; i < servers.length; i++) {
            let tempServer = servers[i];
            if (primaryModel.sameEntry(tempServer)) {
               //Registered servers are already stored in local storage, but may not have secondaryServers property.
               //If not, initiate first then add value to it
               if (!servers[i].secondaryServers) {
                  servers[i].secondaryServers = [];
               } else {
                  for (let j = 0; j < servers[i].secondaryServers.length; j++) {
                     if (secondaryServer === servers[i].secondaryServers[j]) {
                        servers[i].secondaryServers.splice(j, 1);
                        break;
                     }
                  }
               }
               servers[i].secondaryServers.unshift(secondaryServer);
               servers[i].updateTime = new Date().toISOString();
            }
         }

         this.setServer(servers);
         resolve(servers);
      });
   };

   public getOriginServer = (server: string): Promise<string> => {
      if (!server) {
         throw "Illegal server address passed in!";
      }

      return new Promise((resolve, reject) => {
         this.policyModel.getModel().then((model) => {
            if (model) {
               const commonAdminSettings = model.getCommonAdminSettings();
               this.hideSecondaryServer = commonAdminSettings.hideSecondaryServer !== false;
            }

            if (!this.hideSecondaryServer) {
               resolve("");
               return;
            }

            server = server.toLowerCase();
            let tempServers = [];

            let servers = this.getServer();
            for (let i = 0; i < servers.length; i++) {
               if (servers[i].secondaryServers) {
                  for (let j = 0; j < servers[i].secondaryServers.length; j++) {
                     if (server === servers[i].secondaryServers[j]) {
                        tempServers.unshift(servers[i]);
                     }
                  }
               }
            }
            if (tempServers.length === 0) {
               resolve("");
               return;
            } else {
               // Return the most recent server.
               tempServers.sort((a, b) => {
                  return b.updateTime > a.updateTime ? 1 : -1;
               });
               let originServer: string = tempServers[0].address;
               resolve(originServer);
               return;
            }
         });
      });
   };

   public removeRegisteredServer = (server) => {
      if (!server) {
         throw "Null server object is passed in!";
      }

      return new Promise((resolve, reject) => {
         const model = this.chromeServerModel.getModel(
            server.address,
            server.autoConnect,
            server.description,
            server.secondaryServers,
            server.updateTime,
            server.isTitan
         );

         let servers = this.getServer();
         if (!servers) {
            servers = [];
         }

         let serverToDelete = null;
         for (let i = 0; i < servers.length; i++) {
            const tempServer = servers[i];

            // we don't use the equal here since the previous release (< 4.9)
            // doesn't store the description in to the storage.
            if (model.sameEntry(tempServer)) {
               // Found it and delete it
               serverToDelete = i;
               break;
            }
         }

         if (serverToDelete === null) {
            // No found server to delete
            resolve(servers);
            return;
         }

         // Delete the server and save back to storage
         servers.splice(serverToDelete, 1);
         this.setServer(servers);

         resolve(servers);
      });
   };

   public getPlainAddress = (brokerUrl) => {
      if (!brokerUrl) {
         throw "Illegal server address passed in!";
      }

      brokerUrl = brokerUrl.toLowerCase();
      // truncate prefix
      if (brokerUrl.indexOf(ChromeServerService.HTTPS_PREFIX) === 0) {
         brokerUrl = brokerUrl.slice(ChromeServerService.HTTPS_PREFIX.length);
      } else if (brokerUrl.indexOf(ChromeServerService.HTTP_PREFIX) === 0) {
         brokerUrl = brokerUrl.slice(ChromeServerService.HTTP_PREFIX.length);
      }

      // truncate suffix
      while (brokerUrl.endsWith("/")) {
         brokerUrl = brokerUrl.substring(0, brokerUrl.length - 1);
      }

      return brokerUrl;
   };

   public isServerValid = (server) => {
      if (!server) {
         return false;
      }

      /**
       * return true always for now.
       * In the feature UDP mode, the address should first be checked before
       * connect to it.
       */
      return true;
   };

   public updateRegisteredServer(server) {}
}
